import type { Dictionary } from '@ngrx/entity';
import type { TimesheetModel } from '@reducers/orm/timesheet/timesheet.model';
import { featureFlag, type FeatureService } from '@sb/shared/feature';
import type { PlainDateRange } from '@shiftbase-com/ts-shared/utilities';
import { catchError, map, of, switchMap } from 'rxjs';

import { lookupPriority } from '../../list/build-categorized-groups';
import type { TimesheetHelperService } from '../timesheet-helper.service';
import type { TimesheetPriorityApiService } from './timesheet-priority-api.service';
import { isPriorityShift } from './timesheet-priority.helper';
import type { PriorityEntities, PriorityEntry, PriorityShift, PriorityTimesheet } from './timesheet-priority.models';

/**
 * The collaborators the priority load needs. Passed rather than injected: the list view loads on
 * every route emission and the payroll flow on every scope change, both outside an injection
 * context.
 */
export interface TimesheetPriorityDeps {
  priorityApi: TimesheetPriorityApiService;
  timesheetHelper: TimesheetHelperService;
}

/** What one priority fetch covers, kept whole so a mutation can repeat the same one. */
export interface PriorityRequest {
  range: PlainDateRange;
  departmentIds: string[];
}

/** `department_ids` is required by the endpoint and must be non-empty. */
export function isPriorityFromApiEnabled(featureService: FeatureService, departmentIds: string[]): boolean {
  return featureService.isEnabled(featureFlag.TMP_TIMESHEET_PRIORITY_FROM_API) && departmentIds.length > 0;
}

/**
 * The range's priority entities, or `null` when the endpoint could not answer — in which case the
 * conflicts it would have carried are loaded instead, so a caller falling back to local
 * categorization still has them.
 */
export function loadPriorityEntities(deps: TimesheetPriorityDeps, range: PlainDateRange, departmentIds: string[]) {
  return deps.priorityApi.listForRange(range, departmentIds).pipe(
    map(toPriorityEntities),
    switchMap((entities) =>
      deps.timesheetHelper
        .enrichConflictingTimesheets(
          Object.values(entities.timesheets)
            .filter((entry): entry is PriorityTimesheet => entry !== undefined)
            .map((entry) => ({ id: entry.timesheetId, conflicts: entry.conflicts })),
        )
        .pipe(map(() => entities)),
    ),
    catchError(() => loadRangeConflicts(deps, range, departmentIds).pipe(map(() => null))),
  );
}

export function loadRangeConflicts(deps: TimesheetPriorityDeps, range: PlainDateRange, departmentIds: string[]) {
  return deps.timesheetHelper
    .loadConflicts({ from: range.start, to: range.end, departmentId: departmentIds })
    .pipe(catchError(() => of(null)));
}

/** The endpoint withholds rows its read model cannot resolve. */
export function hasRowOmittedFromPriority(
  timesheets: readonly Pick<TimesheetModel, 'id'>[],
  priorityEntities: Dictionary<PriorityTimesheet> | null,
): boolean {
  if (!priorityEntities) {
    return false;
  }

  return timesheets.some((timesheet) => !lookupPriority(priorityEntities, timesheet.id));
}

function toPriorityEntities(entries: PriorityEntry[]): PriorityEntities {
  const timesheets: Dictionary<PriorityTimesheet> = {};
  const shifts: PriorityShift[] = [];

  for (const entry of entries) {
    if (isPriorityShift(entry)) {
      shifts.push(entry);
    } else {
      timesheets[entry.timesheetId] = entry;
    }
  }

  return { timesheets, shifts };
}
